<template>
    <div class="relative" :class="options.wrapperClass">
        <div :class="props.labelInputWrapperClass">
            <slot name="label">
                <label
                    :for="props.id"
                    class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    :class="options.labelClass + srOnly"
                >
                    {{ props.label }}
                    <span
                        class="text-red-500"
                        aria-hidden="true"
                        v-if="options.required"
                    >
                        *
                    </span>
                    <span
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal"
                        v-if="options.optional"
                    >
                        (optional)
                    </span>
                </label>
                <div v-if="options.fakeLabelSpace" class="h-[25px]"></div>
            </slot>
            <div class="relative">
                <Icon
                    :icon="options.icon"
                    class="absolute top-1/2 -translate-y-1/2 z-20"
                    :class="iconClass + options.iconWrapperClass"
                    :svgClass="options.iconSvgClass"
                    :pathClass="options.iconPathClass"
                    v-if="hasIcon"
                />
                <slot name="input">
                    <input
                        :id="props.id"
                        :type="inputType"
                        :name="props.name"
                        :placeholder="props.placeholder"
                        :pattern="pattern"
                        class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 text-slate-800 dark:text-slate-100 text-sm placeholder:text-slate-500 dark:placeholder-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 dark:focus:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        :class="{
                            [options.inputClass]: options.inputClass,
                            [inputError]: true,
                            [hasIcon]: options.icon,
                        }"
                        :disabled="props.disabled"
                        v-model="inputValue"
                        @input="emit('onInput', $event.target)"
                        @blur="emit('onBlur', $event.target)"
                        @focus="emit('onFocus', $event.target)"
                        @change="emit('onChange', $event.target)"
                        v-debounce="debounceTime"
                        :autofocus="props.autofocus"
                        v-maska="maskPattern"
                        :data-maska="maskPattern"
                    />
                </slot>
                <input
                    type="text"
                    class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 text-slate-800 dark:text-slate-100 text-sm placeholder:text-slate-500 dark:placeholder-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 dark:focus:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="{
                        [options.inputClass]: options.inputClass,
                        [inputError]: true,
                        [hasIcon]: options.icon,
                    }"
                    :placeholder="props.placeholder"
                    :name="props.name"
                    v-model="proxyInputValue"
                    v-input-format="inputFormat"
                    v-if="inputFormat"
                    :pattern="pattern"
                    :disabled="props.disabled"
                    v-debounce="debounceTime"
                    :autofocus="props.autofocus"
                />
                <a
                    @click.prevent="showHidden = !showHidden"
                    href="#"
                    class="absolute right-4 top-2.5"
                    role="button"
                    v-if="options.toggleHidden"
                >
                    <Icon icon="eye-off" v-if="!showHidden" />
                    <Icon icon="eye-on" v-if="showHidden" />
                </a>
            </div>
        </div>
        <slot name="error">
            <transition name="fade">
                <div
                    class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                    role="alert"
                    v-if="componentError"
                >
                    <Icon icon="caution" />
                    {{ componentError }}
                </div>
            </transition>
        </slot>
        <slot name="hint">
            <div class="text-slate-500 dark:text-slate-400 text-xs py-1.5" v-if="props.hint">
                {{ props.hint }}
            </div>
        </slot>
    </div>
</template>
<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import InputFormat from '@/directives/input-format';
import Icon from '@/components/ui/icon/icon.vue';
import { vMaska } from 'maska/vue';

const vInputFormat = InputFormat;
const inputValue = defineModel('inputValue');
const proxyInputValue = ref(null);

const props = defineProps({
    type: { type: String, default: 'text' },
    name: { type: String },
    label: { type: String, default: '' },
    id: { type: String, required: true },
    placeholder: { type: String, default: '' },
    pattern: { type: String, default: '' },
    step: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    hint: { type: [String, null], default: null },
    errors: { type: Object, default: null },
    error: { type: [String, null], default: null },
    options: { type: Object, default: null },
    autofocus: { type: Boolean, default: false },
    inputFormat: { type: String, default: '' }, // thousands,
    maskPattern: { type: String, default: null },
});

const validOptionKeys = [
    'optional', // Adds optional part of label
    'required', // Marks the label with a required asterisk
    'wrapperClass', // Covers the entire component
    'labelInputWrapperClass', // Covers the label and input
    'labelClass', // Covers the label
    'inputClass', // Covers the input
    'hideLabel', // Applies sr-only class to the label
    'icon', // Icon name for input field ( building, search... )
    'iconPosition', // Icon position for input field. left | right
    'iconSvgClass',
    'iconPathClass',
    'iconWrapperClass',
    'debounceTime', // Debounce time for input field
    'toggleHidden', // (bool) Toggle password visibility
    'validatePassword', // (bool) Validate password
    'fakeLabelSpace', // (bool) Adds a fake label space
];

const settings = {
    optional: false,
    required: false,
    wrapperClass: '',
    labelInputWrapperClass: '',
    labelClass: '',
    inputClass: '',
    hideLabel: false,
    icon: null,
    iconPosition: 'left', // left | right
    iconSvgClass: '',
    iconPathClass: '',
    iconWrapperClass: '',
    debounceTime: 200,
    toggleHidden: false,
    validatePassword: false,
    fakeLabelSpace: false,
};

watch(proxyInputValue, (val) => {
    const unformattedValue = InputFormat.unformat(val);
    inputValue.value = unformattedValue;
});

const showHidden = ref(false);

// Events will return $event.target.
// If value is needed use [el].value on the method callback of the component.
// @onblur(handleOnBlur) ... handleOnBlur(el) { ... el.value }
const emit = defineEmits(['onInput', 'onFocus', 'onBlur', 'onChange']);

const validIconPositions = ['left', 'right'];

// Merge default settings and props.options.
// props.options takes precedence.
const options = computed(() => {
    if (props.options) {
        for (const key in props.options) {
            if (!validOptionKeys.includes(key)) {
                throw new Error(`Invalid config key: ${key}`);
            }
        }
    }

    return { ...settings, ...props.options };
});

/**
 *  Pass in props `error` or `errors`.
 *  `error` is priority.
 *  errors[key name] comes from laravel backend validation.
 *  error="Please enter a valid email address."
 *  :errors="{ email: "Please enter a valid email address.", name: "Please enter a name" }"
 *  When `errors` is used it will only use the matching key in the object.
 */
const componentError = computed(() => {
    if (props.error) {
        return props.error;
    }

    if (props.errors && props.errors[props.name]) {
        return Array.isArray(props.errors[props.name])
            ? props.errors[props.name][0]
            : props.errors[props.name];
    }

    return null;
});

const srOnly = computed(() => {
    return options.value.hideLabel ? ' sr-only' : '';
});

const inputError = computed(() => {
    return componentError.value ? ' !border-red-500 ' : '';
});

const hasIcon = computed(() => {
    if (options.value.icon && options.value.icon.length > 0) {
        return options.value.iconPosition === 'right'
            ? '!pl-1.5 !pr-10'
            : '!pr-1.5 !pl-10';
    }

    return null;
});

const inputType = computed(() => {
    if (props.type === 'password') {
        if (options.value.toggleHidden) {
            return showHidden.value ? 'text' : 'password';
        }
    }

    if (props.inputFormat) {
        return 'hidden';
    }

    return props.type;
});

const iconClass = computed(() => {
    let classes = '';
    let disabledClass = '';
    if (props.disabled) {
        disabledClass = ' opacity-50 ';
    }
    if (options.value.icon && options.value.icon.length > 0) {
        if (!validIconPositions.includes(options.value.iconPosition)) {
            throw new Error(
                "Invalid icon position. Must be 'left' or 'right'."
            );
        }
        classes +=
            options.value.iconPosition === 'right'
                ? ' right-3 pointer-events-none '
                : ' left-3 pointer-events-none ';
    }

    return classes + disabledClass;
});

const debounceTime = computed(() => {
    return options.value.debounceTime;
});

onMounted(() => {
    if (inputValue.value && props.inputFormat) {
        proxyInputValue.value = InputFormat.format(
            props.inputFormat,
            inputValue.value
        );
    }
});
</script>
